import React, { useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    Button,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Collapse,
    Divider,
    IconButton,
    useMediaQuery,
    useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import ParkingLocations from './ParkingLocations';
import PendingApprovals from './PendingApprovals';
import Vendors from './Vendors';
import ActiveSessions from './ActiveSessions';
import Users from './Users';
import UserRoles from './UserRoles';
import Reports from './Reports';
import UserLookup from './UserLookup';
import VendorLicensePlateLookup from './VendorLicensePlateLookup';
import ChangePassword from './ChangePassword';  // Add this import

const drawerWidth = 240;
const mobileDrawerWidth = 200;

export default function AdminDashboard() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedPage, setSelectedPage] = useState('locations');
    const [mobileOpen, setMobileOpen] = useState(false);
    const [reportsOpen, setReportsOpen] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuClick = (page: string) => {
        setSelectedPage(page);
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
        navigate('/login');
    };

    const drawerContent = (
        <>
            <Toolbar />
            <List>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'locations'}
                        onClick={() => handleMenuClick('locations')}
                    >
                        <ListItemText primary="Parking Locations" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'approvals'}
                        onClick={() => handleMenuClick('approvals')}
                    >
                        <ListItemText primary="Pending Approvals" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'sessions'}
                        onClick={() => handleMenuClick('sessions')}
                    >
                        <ListItemText primary="Active Sessions" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'users'}
                        onClick={() => handleMenuClick('users')}
                    >
                        <ListItemText primary="Users" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'vendors'}
                        onClick={() => handleMenuClick('vendors')}
                    >
                        <ListItemText primary="Partners" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'roles'}
                        onClick={() => handleMenuClick('roles')}
                    >
                        <ListItemText primary="User Roles" />
                    </ListItemButton>
                </ListItem>

                <Divider sx={{ my: 1 }} />

                <ListItem disablePadding>
                    <ListItemButton onClick={() => setReportsOpen(!reportsOpen)}>
                        <ListItemText primary="Reports" />
                        {reportsOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>
                <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <ListItemButton
                            sx={{ pl: 4 }}
                            selected={selectedPage === 'rpt-revenue'}
                            onClick={() => handleMenuClick('rpt-revenue')}
                        >
                            <ListItemText primary="Revenue" />
                        </ListItemButton>
                        <ListItemButton
                            sx={{ pl: 4 }}
                            selected={selectedPage === 'rpt-sessions'}
                            onClick={() => handleMenuClick('rpt-sessions')}
                        >
                            <ListItemText primary="Sessions" />
                        </ListItemButton>
                        <ListItemButton
                            sx={{ pl: 4 }}
                            selected={selectedPage === 'rpt-cards'}
                            onClick={() => handleMenuClick('rpt-cards')}
                        >
                            <ListItemText primary="Card Transactions" />
                        </ListItemButton>
                        <ListItemButton
                            sx={{ pl: 4 }}
                            selected={selectedPage === 'rpt-users'}
                            onClick={() => handleMenuClick('rpt-users')}
                        >
                            <ListItemText primary="User Lookup" />
                        </ListItemButton>
                        <ListItemButton
                            sx={{ pl: 4 }}
                            selected={selectedPage === 'rpt-plate'}
                            onClick={() => handleMenuClick('rpt-plate')}
                        >
                            <ListItemText primary="License Plate Lookup" />
                        </ListItemButton>
                    </List>
                </Collapse>

                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedPage === 'change-password'}
                        onClick={() => handleMenuClick('change-password')}
                    >
                        <ListItemText primary="Change Password" />
                    </ListItemButton>
                </ListItem>
            </List>
        </>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` }
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{
                            flexGrow: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}
                    >
                        ARCS Admin Dashboard
                    </Typography>
                    <Button color="inherit" onClick={handleLogout}>
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Mobile drawer - only shows when hamburger menu is tapped */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: mobileDrawerWidth },
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Desktop drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
                }}
                open
            >
                {drawerContent}
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 1.5, sm: 2, md: 3 },
                    width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` }
                }}
            >
                <Toolbar />
                {selectedPage === 'locations' && <ParkingLocations />}
                {selectedPage === 'approvals' && <PendingApprovals />}
                {selectedPage === 'sessions' && <ActiveSessions />}
                {selectedPage === 'users' && <Users />}
                {selectedPage === 'vendors' && <Vendors />}
                {selectedPage === 'roles' && <UserRoles />}
                {selectedPage === 'rpt-revenue' && <Reports key="rev" fixedType="revenue" />}
                {selectedPage === 'rpt-sessions' && <Reports key="sess" fixedType="sessions" />}
                {selectedPage === 'rpt-cards' && <Reports key="cards" fixedType="cards" />}
                {selectedPage === 'rpt-users' && <UserLookup />}
                {selectedPage === 'rpt-plate' && <VendorLicensePlateLookup admin />}
                {selectedPage === 'change-password' && <ChangePassword />}
            </Box>
        </Box>
    );
}
